import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, Calculator, Minus, ShieldCheck, Wallet } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

type DpaType = "forgivable" | "deferred" | "amortizing" | "grant";
type RefiPurpose = "rate_and_term" | "cash_out";

type Factor = {
  label: string;
  detail: string;
  lean: "subordinate" | "payoff" | "neutral";
  weight: number;
};

const DPA_TYPE_LABELS: Record<DpaType, string> = {
  forgivable: "Forgivable second (burns off over time)",
  deferred: "Deferred 0% second (repaid in full later)",
  amortizing: "Amortizing second (you pay it monthly)",
  grant: "Grant, already fully forgiven",
};

const currency = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

function monthlyPayment(principal: number, annualRate: number, years = 30) {
  const r = annualRate / 100 / 12;
  const n = years * 12;
  if (principal <= 0) return 0;
  if (r === 0) return principal / n;
  return (principal * r) / (1 - Math.pow(1 + r, -n));
}

const num = (v: string) => {
  const parsed = Number(v.replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

export default function DpaPayoffVsSubordinateCalculator() {
  const [dpaType, setDpaType] = useState<DpaType>("forgivable");
  const [refiPurpose, setRefiPurpose] = useState<RefiPurpose>("rate_and_term");
  const [dpaBalance, setDpaBalance] = useState("12000");
  const [monthsLeft, setMonthsLeft] = useState("36");
  const [loanAmount, setLoanAmount] = useState("320000");
  const [currentRate, setCurrentRate] = useState("7.25");
  const [newRate, setNewRate] = useState("6.25");
  const [cashAvailable, setCashAvailable] = useState("8000");
  const [yearsInHome, setYearsInHome] = useState("7");
  const [subFee, setSubFee] = useState("350");
  const [refiCosts, setRefiCosts] = useState("5500");
  const [touched, setTouched] = useState(false);

  const result = useMemo(() => {
    const balance = num(dpaBalance);
    const months = dpaType === "forgivable" ? num(monthsLeft) : 0;
    const loan = num(loanAmount);
    const cash = num(cashAvailable);
    const years = Math.max(num(yearsInHome), 0);
    const fee = num(subFee);
    const costs = num(refiCosts);

    const monthlySavings = Math.max(
      monthlyPayment(loan, num(currentRate)) - monthlyPayment(loan, num(newRate)),
      0,
    );
    const breakEvenMonths = monthlySavings > 0 ? (costs + fee) / monthlySavings : Infinity;
    const forgivenessAtRisk = dpaType === "forgivable" ? balance : 0;
    const cashShortfall = Math.max(balance - cash, 0);

    const factors: Factor[] = [];

    if (dpaType === "grant") {
      factors.push({
        label: "Nothing left to repay",
        detail:
          "A fully forgiven grant has no lien balance to pay off or subordinate. Confirm the release was recorded, then refinance normally.",
        lean: "neutral",
        weight: 0,
      });
    } else if (dpaType === "forgivable" && months > 0) {
      factors.push({
        label: `${months} months of forgiveness still running`,
        detail: `Paying off early can forfeit up to ${currency(forgivenessAtRisk)} of assistance you were on track to have wiped out. Subordinating keeps that clock alive in most programs — get the confirmation in writing.`,
        lean: "subordinate",
        weight: months >= 24 ? 3 : 2,
      });
    } else if (dpaType === "forgivable") {
      factors.push({
        label: "Forgiveness period is over",
        detail:
          "With the clock finished, there is little upside to keeping the lien on title. Payoff (if a balance remains) simplifies your next sale or refinance.",
        lean: "payoff",
        weight: 1,
      });
    } else if (dpaType === "amortizing") {
      factors.push({
        label: "You are already paying this second monthly",
        detail:
          "An amortizing second carries real interest and a payment. If you can retire it without draining reserves, payoff removes both the payment and the subordination approval risk.",
        lean: "payoff",
        weight: 2,
      });
    } else {
      factors.push({
        label: "Deferred 0% second, no forgiveness at stake",
        detail:
          "A deferred second costs nothing to carry, so there is no interest savings from paying it early — but it also comes due eventually. Subordination is usually the cheaper move today.",
        lean: "subordinate",
        weight: 1,
      });
    }

    if (dpaType !== "grant") {
      if (refiPurpose === "cash_out") {
        factors.push({
          label: "Cash-out refinance",
          detail:
            "Cash-out is the most common reason a subordination request is declined: pulling equity shrinks the cushion protecting the program's second position. Plan for a payoff as the fallback.",
          lean: "payoff",
          weight: 3,
        });
      } else {
        factors.push({
          label: "Rate-and-term refinance",
          detail:
            "Rate-and-term refinances with a clear net tangible benefit are what subordination policies are written for. Your approval odds are highest here.",
          lean: "subordinate",
          weight: 2,
        });
      }

      if (cashShortfall > 0) {
        factors.push({
          label: `Cash shortfall of ${currency(cashShortfall)}`,
          detail: `Paying off ${currency(balance)} with ${currency(cash)} on hand means rolling the difference into the new loan or walking away from the refinance. Subordination avoids the shortfall entirely.`,
          lean: "subordinate",
          weight: 3,
        });
      } else if (balance > 0) {
        factors.push({
          label: "You can cover the payoff in cash",
          detail: `Your ${currency(cash)} covers the ${currency(balance)} balance with ${currency(cash - balance)} left over, so payoff is a real option rather than a theoretical one. Keep an emergency reserve after closing.`,
          lean: "payoff",
          weight: 1,
        });
      }
    }

    if (monthlySavings > 0) {
      const beText =
        breakEvenMonths <= years * 12
          ? `You break even on ${currency(costs + fee)} of costs in about ${Math.ceil(breakEvenMonths)} months — inside the ${years} years you expect to stay.`
          : `Break-even runs about ${Math.ceil(breakEvenMonths)} months, longer than the ${years} years you expect to stay. The refinance itself may not pay for itself.`;
      factors.push({
        label: `Monthly savings of ${currency(monthlySavings)}`,
        detail: `${beText} Subordination fees are small next to this; a forced payoff is not.`,
        lean: breakEvenMonths <= years * 12 ? "subordinate" : "neutral",
        weight: 1,
      });
    } else {
      factors.push({
        label: "No payment savings at the new rate",
        detail:
          "Without a lower payment, many programs will fail your refinance on a net-tangible-benefit test — and you would be paying costs for no gain. Re-check the rate before deciding anything about the lien.",
        lean: "neutral",
        weight: 0,
      });
    }

    if (dpaType !== "grant" && years <= 3) {
      factors.push({
        label: "Short time horizon in the home",
        detail: `Planning to leave in about ${years} years means a sale — and its repayment trigger — is close. Spending cash to retire the lien now rarely pays off before you sell.`,
        lean: "subordinate",
        weight: 2,
      });
    }

    let subScore = 0;
    let payScore = 0;
    for (const f of factors) {
      if (f.lean === "subordinate") subScore += f.weight;
      if (f.lean === "payoff") payScore += f.weight;
    }

    let recommendation: "subordinate" | "payoff" | "either";
    if (dpaType === "grant") recommendation = "either";
    else if (subScore - payScore >= 1) recommendation = "subordinate";
    else if (payScore - subScore >= 1) recommendation = "payoff";
    else recommendation = "either";

    return {
      factors,
      recommendation,
      subScore,
      payScore,
      monthlySavings,
      breakEvenMonths,
      cashShortfall,
      forgivenessAtRisk,
      balance,
    };
  }, [
    dpaType,
    refiPurpose,
    dpaBalance,
    monthsLeft,
    loanAmount,
    currentRate,
    newRate,
    cashAvailable,
    yearsInHome,
    subFee,
    refiCosts,
  ]);

  const headline =
    result.recommendation === "subordinate"
      ? "Lean toward asking for subordination"
      : result.recommendation === "payoff"
        ? "Lean toward paying the assistance off"
        : "It is close — either path can work";

  const summary =
    result.recommendation === "subordinate"
      ? "Your inputs point at keeping the assistance in place behind the new first mortgage. Request the program's written subordination policy before you apply to refinance."
      : result.recommendation === "payoff"
        ? "Your inputs point at retiring the second lien at closing. Get a written payoff statement with a good-through date from the servicer named on your note."
        : "No single factor dominates. Price both paths with your loan officer and the program administrator before you lock.";

  const onCalculate = () => {
    setTouched(true);
    trackEvent("dpa_calculator_run", {
      dpa_type: dpaType,
      refi_purpose: refiPurpose,
      recommendation: result.recommendation,
    });
  };

  return (
    <Card className="border-border">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start gap-3 mb-5">
          <Calculator className="h-5 w-5 text-primary mt-0.5 shrink-0" aria-hidden="true" />
          <div>
            <h3 className="text-lg font-bold text-foreground">
              Pay off or subordinate? Run your own numbers
            </h3>
            <p className="text-sm text-muted-foreground">
              Educational estimate only. Your program's written policy and your servicer's payoff
              statement control the real outcome.
            </p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label htmlFor="dpa-type">Type of assistance</Label>
            <Select value={dpaType} onValueChange={(v) => setDpaType(v as DpaType)}>
              <SelectTrigger id="dpa-type" className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(DPA_TYPE_LABELS) as DpaType[]).map((k) => (
                  <SelectItem key={k} value={k}>
                    {DPA_TYPE_LABELS[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="dpa-balance">Assistance balance</Label>
            <Input
              id="dpa-balance"
              inputMode="decimal"
              className="mt-1.5"
              value={dpaBalance}
              onChange={(e) => setDpaBalance(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="months-left">Months of forgiveness left</Label>
            <Input
              id="months-left"
              inputMode="numeric"
              className="mt-1.5"
              value={monthsLeft}
              disabled={dpaType !== "forgivable"}
              onChange={(e) => setMonthsLeft(e.target.value)}
            />
          </div>

          <div className="sm:col-span-2">
            <Label htmlFor="refi-purpose">Refinance you are considering</Label>
            <Select value={refiPurpose} onValueChange={(v) => setRefiPurpose(v as RefiPurpose)}>
              <SelectTrigger id="refi-purpose" className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rate_and_term">Rate-and-term (no cash out)</SelectItem>
                <SelectItem value="cash_out">Cash-out</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="loan-amount">New first mortgage amount</Label>
            <Input
              id="loan-amount"
              inputMode="decimal"
              className="mt-1.5"
              value={loanAmount}
              onChange={(e) => setLoanAmount(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="cash-available">Cash you could bring to closing</Label>
            <Input
              id="cash-available"
              inputMode="decimal"
              className="mt-1.5"
              value={cashAvailable}
              onChange={(e) => setCashAvailable(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="current-rate">Current rate (%)</Label>
            <Input
              id="current-rate"
              inputMode="decimal"
              className="mt-1.5"
              value={currentRate}
              onChange={(e) => setCurrentRate(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="new-rate">New rate (%)</Label>
            <Input
              id="new-rate"
              inputMode="decimal"
              className="mt-1.5"
              value={newRate}
              onChange={(e) => setNewRate(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="refi-costs">Refinance closing costs</Label>
            <Input
              id="refi-costs"
              inputMode="decimal"
              className="mt-1.5"
              value={refiCosts}
              onChange={(e) => setRefiCosts(e.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="sub-fee">Subordination processing fee</Label>
            <Input
              id="sub-fee"
              inputMode="decimal"
              className="mt-1.5"
              value={subFee}
              onChange={(e) => setSubFee(e.target.value)}
            />
          </div>

          <div className="sm:col-span-2">
            <Label htmlFor="years-home">Years you expect to stay in the home</Label>
            <Input
              id="years-home"
              inputMode="numeric"
              className="mt-1.5"
              value={yearsInHome}
              onChange={(e) => setYearsInHome(e.target.value)}
            />
          </div>
        </div>

        <Button className="mt-5 w-full sm:w-auto" onClick={onCalculate}>
          See my recommended path
          <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
        </Button>

        {touched && (
          <div className="mt-6 border-t border-border pt-6">
            <div className="rounded-lg bg-muted/50 p-4 sm:p-5">
              <div className="flex items-start gap-3">
                {result.recommendation === "payoff" ? (
                  <Wallet className="h-5 w-5 text-primary mt-0.5 shrink-0" aria-hidden="true" />
                ) : (
                  <ShieldCheck className="h-5 w-5 text-primary mt-0.5 shrink-0" aria-hidden="true" />
                )}
                <div>
                  <p className="font-bold text-foreground">{headline}</p>
                  <p className="text-sm text-muted-foreground mt-1">{summary}</p>
                </div>
              </div>

              <dl className="mt-4 grid gap-3 sm:grid-cols-3 text-sm">
                <div>
                  <dt className="text-muted-foreground">Estimated monthly savings</dt>
                  <dd className="font-semibold text-foreground">
                    {currency(result.monthlySavings)}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Break-even</dt>
                  <dd className="font-semibold text-foreground">
                    {Number.isFinite(result.breakEvenMonths)
                      ? `${Math.ceil(result.breakEvenMonths)} months`
                      : "Not reached"}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Forgiveness at risk</dt>
                  <dd className="font-semibold text-foreground">
                    {currency(result.forgivenessAtRisk)}
                  </dd>
                </div>
              </dl>
            </div>

            <h4 className="mt-6 mb-3 font-semibold text-foreground">Why this came out that way</h4>
            <ul className="space-y-3">
              {result.factors.map((f) => (
                <li
                  key={f.label}
                  className="rounded-lg border border-border p-4 flex items-start gap-3"
                >
                  {f.lean === "subordinate" ? (
                    <ShieldCheck className="h-4 w-4 text-primary mt-1 shrink-0" aria-hidden="true" />
                  ) : f.lean === "payoff" ? (
                    <Wallet className="h-4 w-4 text-primary mt-1 shrink-0" aria-hidden="true" />
                  ) : (
                    <Minus className="h-4 w-4 text-muted-foreground mt-1 shrink-0" aria-hidden="true" />
                  )}
                  <div>
                    <p className="font-medium text-foreground">
                      {f.label}
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        {f.lean === "subordinate"
                          ? "points to subordinate"
                          : f.lean === "payoff"
                            ? "points to pay off"
                            : "context only"}
                      </span>
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">{f.detail}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
